import { useCallback, useEffect, useState } from "react";

let activeMenuId = null;
const closeCallbacks = new Map();

/**
 * Exclusive hover-menu state for action-items header controls (filter, sort, etc.).
 * Opening one menu immediately closes any other open menu.
 *
 * @example
 * const { menuOpen, openMenu, handleMouseLeave } = useActionItemsHeaderHoverMenu("sort");
 * // openMenu() — closes "filter" if open, then opens sort
 * // handleMouseLeave() — closes sort immediately (no delay)
 */
export const useActionItemsHeaderHoverMenu = (menuId) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    if (activeMenuId === menuId) {
      activeMenuId = null;
    }
  }, [menuId]);

  const openMenu = useCallback(() => {
    if (activeMenuId && activeMenuId !== menuId) {
      closeCallbacks.get(activeMenuId)?.();
    }
    activeMenuId = menuId;
    setMenuOpen(true);
  }, [menuId]);

  useEffect(() => {
    closeCallbacks.set(menuId, closeMenu);
    return () => {
      closeCallbacks.delete(menuId);
      if (activeMenuId === menuId) {
        activeMenuId = null;
      }
    };
  }, [menuId, closeMenu]);

  const handleMouseLeave = useCallback(() => {
    closeMenu();
  }, [closeMenu]);

  return { menuOpen, openMenu, closeMenu, handleMouseLeave };
};
